import { components } from "@/api-schema";
import { useJobFunctionSearchQuery } from "@/common/api/job-function/use-job-function-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

export type QueryBody = components["schemas"]["JobFunctionSearchBody"];

const FILTER_PAGE_SIZE = 50;

// Custom hook to fetch job function data with pagination
function useJobFunctionFilterData(body: QueryBody): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: jobFunctions,
    isPending: isLoading,
    error,
  } = useJobFunctionSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
      },
      body,
    },
  });

  const options =
    jobFunctions?.data
      .map((element) => ({
        id: element.uuid ?? "",
        name: element.name ?? "",
      }))
      .filter((option) => option.id && option.name)
      .filter((option, index, array) => array.findIndex((item) => item.id === option.id) === index) || [];

  const loadedCount = jobFunctions?.data?.length ?? 0;
  const total = jobFunctions?.meta?.total ?? 0;

  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: {
      options,
      isLoading,
      error,
    },
    pagination,
  };
}

export class JobFunctionFilter extends Filter<QueryBody, unknown> {
  readonly id: string = "jobFunctions";
  readonly labelSingular = "common:JobFunction";
  readonly labelPlural = "ram:JobFunctions";
  readonly placeholder = "ram:SelectJobFunctions";
  readonly searchPlaceholder = "common:SearchJobFunctions";
  readonly emptyText = "common:NoJobFunctionsFound";

  constructor({ id, body = {} }: { id?: string; body?: Partial<QueryBody> } = {}) {
    super();
    if (id) this.id = id;
    this.body = body as QueryBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <JobFunctionFilterComponent filter={this} {...props} />;
  }
}

function JobFunctionFilterComponent({ filter, ...props }: { filter: JobFunctionFilter } & BaseFilterProps) {
  const { data, pagination } = useJobFunctionFilterData(filter.body);

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
